import * as React from "react";
import {option, Option} from "ts-option";
import {FormField} from "../../../validation/FormField";
import FormGroup from "../../../hiberUiKit/src/molecules/FormGroup/FormGroup";
import RadioButton from "../../../hiberUiKit/src/atoms/RadioButton/RadioButton";
import CurrencyInput from "../../../hiberUiKit/src/atoms/Input/CurrencyInput";
import {CreditValueCapMode} from "../../../model/partnerBrand/CreditValueCapMode";

interface Props {
	/**
	 * Label of the whole group.
	 */
	label: string;
	/**
	 * Which of the three states this partner is on.
	 */
	modeField: FormField<CreditValueCapMode>;
	/**
	 * The partner's own amount, in euro, used only alongside {@link CreditValueCapMode.FIXED}.
	 */
	amountField: FormField<string>;
	/**
	 * What the partner inherits, in cents, read from the parent Brand. Empty until a brand is picked on the
	 * create screen.
	 */
	inheritedCapCents: Option<number>;
	/**
	 * Name of the parent Brand, so that the inherit option can say where the amount comes from.
	 */
	brandName: Option<string>;
	/**
	 * Currency the rest of the payout amounts on this screen are shown in.
	 */
	currencySymbol: string;
	/**
	 * Cross-field error, e.g. a fixed cap with no amount behind it.
	 */
	error?: string;
	/**
	 * Called for either field.
	 */
	onChange: (field: FormField<any>) => void;
}

/**
 * The three states a partner's mileage payout cap can be in.
 *
 * One component rather than two form groups because the amount only means anything alongside the middle
 * option, so the amount box shows only while that option is selected: one that could be filled in under
 * another option would be a promise the backend does not keep - it reads the amount only for
 * {@link CreditValueCapMode.FIXED}.
 *
 * The inherited amount is spelled out on the first option on purpose: "use the Brand cap" tells an admin
 * nothing about what this partner will actually be paid, and the alternative is opening the Brand screen
 * in another tab to find out.
 */
export default class CreditValueCapFormGroup extends React.Component<Props, {}> {

	render() {
		const mode = this.props.modeField.getOptionalValue().getOrElse(CreditValueCapMode.INHERIT);
		return (
			<FormGroup label={this.props.label} error={this.props.error || this.props.modeField.firstError().orNull}>
				<div style={{border: "1px solid #d9d9d9", borderRadius: "3px"}}>
					{this.renderOption(mode, CreditValueCapMode.INHERIT, "Use the Brand cap", this.inheritedDescription())}
					{this.renderOption(mode, CreditValueCapMode.FIXED, "Cap at a different amount")}
					{mode === CreditValueCapMode.FIXED &&
					<div style={{padding: "0 13px 13px 34px"}}>
						<div className={"input-group"} style={{maxWidth: "210px"}}>
							<CurrencyInput value={this.props.amountField.value}
										   placeholder={"Enter cap amount"}
										   onChange={(value: Option<string>) =>
											   this.props.onChange(this.props.amountField.updateValue(value))}/>
						</div>
					</div>}
					{this.renderOption(mode, CreditValueCapMode.NONE, "No cap")}
				</div>
			</FormGroup>
		);
	}

	private renderOption(selected: CreditValueCapMode, mode: CreditValueCapMode, label: string, description?: string) {
		return (
			<div style={{padding: "10px 13px", borderTop: mode === CreditValueCapMode.INHERIT ? "none" : "1px solid #e6e8ee"}}>
				<RadioButton radioButtonOption={{label, value: mode, disabled: false}}
							 selected={selected === mode}
							 onChange={() => this.props.onChange(this.props.modeField.updateValue(option(mode)))}/>
				{description && <div style={{fontSize: "12.5px", color: "#5b6478", paddingLeft: "22px"}}>{description}</div>}
			</div>
		);
	}

	/**
	 * Names the inherited amount, and says plainly when it cannot be named yet - which on the create screen
	 * is until a parent brand is chosen.
	 */
	private inheritedDescription(): string {
		const from = this.props.brandName.map(name => ` from ${name}`).getOrElse("");
		return this.props.inheritedCapCents
			.map(cents => `Currently ${this.props.currencySymbol}${(cents / 100).toFixed(2)}${from}`)
			.getOrElse("Takes the amount set on the parent Brand");
	}
}
